import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { StarIcon, HeartIcon, ShareIcon, ShoppingCartIcon, TruckIcon, ShieldCheckIcon, RefreshCwIcon, MinusIcon, PlusIcon, CheckIcon } from 'lucide-react';
import { products } from '../data/products';
import { formatPrice, getStockStatus } from '../utils/formatters';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { ProductGrid } from '../components/products/ProductGrid';
export function ProductDetailPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = products.find(p => p.slug === slug);
  const {
    addItem
  } = useCart();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  if (!product) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-1511" data-mp="2qeexswo7bzd3">
        <div className="text-center" data-id="element-1512" data-mp="221dppg3l8vk6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1513" data-mp="222fe0m9m7eju">
            Product Not Found
          </h1>
          <p className="text-gray-600 mb-4" data-id="element-1514" data-mp="1x55gejc2nwkx">
            The product you're looking for doesn't exist.
          </p>
          <Link to="/products" data-id="element-1515" data-mp="263qua16lmnfd">
            <Button variant="primary" data-id="element-1516" data-mp="263hcv1zmdola">Browse Products</Button>
          </Link>
        </div>
      </div>;
  }
  const stockStatus = getStockStatus(product.stockQuantity);
  const relatedProducts = products.filter(p => p.categorySlug === product.categorySlug && p.id !== product.id).slice(0, 4);
  const handleAddToCart = () => {
    addItem(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };
  return <div className="min-h-screen bg-gray-50 py-6" data-id="element-1517" data-mp="2psjcygbuyq4i">
      <div className="max-w-7xl mx-auto px-4" data-id="element-1518" data-mp="33qpv8o4bqhj2">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-6" aria-label="Breadcrumb" data-id="element-1519" data-mp="2ysfve07nmsr4">
          <ol className="flex items-center gap-2 flex-wrap" data-id="element-1520" data-mp="2yqbn762q37l1">
            <li data-id="element-1521" data-mp="1wgbbdmnsf2dx">
              <Link to="/" className="hover:text-primary-600" data-id="element-1522" data-mp="1wgl076x3j3i1">
                Home
              </Link>
            </li>
            <li data-id="element-1523" data-mp="20e91crpdtsid">/</li>
            <li data-id="element-1524" data-mp="25fycs8gio86z">
              <Link to="/products" className="hover:text-primary-600" data-id="element-1525" data-mp="25fg7ynfsglng">
                Products
              </Link>
            </li>
            <li data-id="element-1526" data-mp="2a7zl5jakderq">/</li>
            <li data-id="element-1527" data-mp="2a89dvemzu014">
              <Link to={`/products?category=${product.categorySlug}`} className="hover:text-primary-600" data-id="element-1528" data-mp="2f5hv9d0bi01b">
                {product.category}
              </Link>
            </li>
            <li data-id="element-1529" data-mp="2k6p31c2gmz7z">/</li>
            <li className="text-gray-900 font-medium truncate max-w-[200px]" data-id="element-1530" data-mp="2o6frh94il39c">
              {product.name}
            </li>
          </ol>
        </nav>

        {/* Product section */}
        <div className="bg-white rounded-xl shadow-card p-6 mb-8" data-id="element-1531" data-mp="2t2wqwzh5z41q">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1532" data-mp="ttpziwxpi44l">
            {/* Images */}
            <div data-id="element-1533" data-mp="yrrsc9kxpht9">
              <div className="aspect-square rounded-xl overflow-hidden bg-gray-50 mb-4" data-id="element-1534" data-mp="ytcat0tz5aar">
                <img src={product.images[selectedImage]} alt={product.name} className="w-full h-full object-cover" data-id="element-1535" data-mp="r91pl0wmol2x" />
              </div>
              {product.images.length > 1 && <div className="flex gap-3" data-id="element-1536" data-mp="hh5n7dnie8sw">
                  {product.images.map((image, index) => <button key={index} onClick={() => setSelectedImage(index)} className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${selectedImage === index ? 'border-primary-600' : 'border-gray-200'}`} data-id="element-1537" data-mp="cjclawt8o0yf" data-mpi={index}>
                      <img src={image} alt="" className="w-full h-full object-cover" data-id="element-1538" data-mp="158t91h5wytob" />
                    </button>)}
                </div>}
            </div>

            {/* Details */}
            <div data-id="element-1539" data-mp="w7lbx2f4vyvt">
              <div className="flex items-start justify-between gap-4" data-id="element-1540" data-mp="w61wfe1axrv3">
                <div data-id="element-1541" data-mp="3kz5sum5le23c">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-1542" data-mp="3kzfiu9qn0991">{product.brand}</p>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-1543" data-mp="3gwswgocqjgj8">
                    {product.name}
                  </h1>
                </div>
                <div className="flex gap-2" data-id="element-1544" data-mp="18mjizljdxo87">
                  <button className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:text-red-500 hover:border-red-200 transition-colors" aria-label="Add to wishlist" data-id="element-1545" data-mp="1dg4ff95bya5a">
                    <HeartIcon className="w-5 h-5" data-id="element-1546" data-mp="13oqcere66s7e" />
                  </button>
                  <button className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:text-primary-600 hover:border-primary-200 transition-colors" aria-label="Share product" data-id="element-1547" data-mp="1reudaqzz4dup">
                    <ShareIcon className="w-5 h-5" data-id="element-1548" data-mp="1wbktrffoxbgn" />
                  </button>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-4" data-id="element-1549" data-mp="1mepvmxnbxd2w">
                <div className="flex items-center" data-id="element-1550" data-mp="6z0khtx6cohu">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-5 h-5 ${i < Math.floor(product.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} data-id="element-1551" data-mp="713k95o4pvzr" data-mpi={i} />)}
                </div>
                <span className="text-sm text-gray-600" data-id="element-1552" data-mp="3fs2elwv8vh9">
                  {product.rating} ({product.reviewCount.toLocaleString()}{' '}
                  reviews)
                </span>
              </div>

              {/* Price */}
              <div className="flex items-center gap-3 mb-4" data-id="element-1553" data-mp="dcwmmg75igvf">
                <span className="text-3xl font-bold text-gray-900" data-id="element-1554" data-mp="8hh95pd22mzj">
                  {formatPrice(product.price)}
                </span>
                {product.originalPrice && product.originalPrice > product.price && <>
                      <span className="text-xl text-gray-400 line-through" data-id="element-1555" data-mp="3bus8npt14zbf">
                        {formatPrice(product.originalPrice)}
                      </span>
                      <Badge variant="error" data-id="element-1556" data-mp="3luaphxdn84z9">-{product.discount}% OFF</Badge>
                    </>}
              </div>

              {/* Stock status */}
              <div className="flex items-center gap-2 mb-6" data-id="element-1557" data-mp="2t6ewb2na8v78">
                <span className={`font-medium ${stockStatus.color}`} data-id="element-1558" data-mp="2t65190ndrxuo">
                  {stockStatus.label}
                </span>
                {product.inStock && <span className="text-sm text-gray-500" data-id="element-1559" data-mp="3mvm94bfs2dkc">
                    ({product.stockQuantity} available)
                  </span>}
              </div>

              {/* Short description */}
              <p className="text-gray-600 mb-6" data-id="element-1560" data-mp="h2wokad58gi">{product.shortDescription}</p>

              {/* Quantity selector */}
              <div className="flex items-center gap-4 mb-6" data-id="element-1561" data-mp="2zwq4rrnusa59">
                <span className="text-sm font-medium text-gray-700" data-id="element-1562" data-mp="33xa2awcc9frx">
                  Quantity:
                </span>
                <div className="flex items-center gap-2" data-id="element-1563" data-mp="38vtn2tf74lip">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors" aria-label="Decrease quantity" data-id="element-1564" data-mp="38yfvev9wv51z">
                    <MinusIcon className="w-4 h-4" data-id="element-1565" data-mp="t45s33jqt32j" />
                  </button>
                  <span className="w-12 text-center font-medium text-lg" data-id="element-1566" data-mp="2dnsrqqjp10pb">
                    {quantity}
                  </span>
                  <button onClick={() => setQuantity(Math.min(product.stockQuantity, quantity + 1))} className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors" aria-label="Increase quantity" data-id="element-1567" data-mp="28phhiephko6k">
                    <PlusIcon className="w-4 h-4" data-id="element-1568" data-mp="1uxb3ay5w8r9r" />
                  </button>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 mb-6" data-id="element-1569" data-mp="1pzrp1y974eht">
                <Button variant="primary" size="lg" className="flex-1" leftIcon={addedToCart ? <CheckIcon className="w-5 h-5" data-id="element-1571" data-mp="1fzr1g04anc7t" /> : <ShoppingCartIcon className="w-5 h-5" data-id="element-1572" data-mp="1g0kbb9yjuhu6" />} onClick={handleAddToCart} disabled={!product.inStock} data-id="element-1570" data-mp="1kxsk4jxt59ir">
                  {addedToCart ? 'Added to Cart!' : 'Add to Cart'}
                </Button>
                <Link to="/checkout" className="flex-1" data-id="element-1573" data-mp="3aeto7q1ykg71">
                  <Button variant="outline" size="lg" fullWidth disabled={!product.inStock} data-id="element-1574" data-mp="2j8fwqny6d8e0">
                    Buy Now
                  </Button>
                </Link>
              </div>

              {/* Features */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-6 border-t border-gray-100" data-id="element-1575" data-mp="2agnrcro0cpga">
                <div className="flex items-center gap-3" data-id="element-1576" data-mp="2ego324jq2mjn">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center" data-id="element-1577" data-mp="2ef3b8moslhph">
                    <TruckIcon className="w-5 h-5 text-primary-600" data-id="element-1578" data-mp="25grnqtj83019" />
                  </div>
                  <div data-id="element-1579" data-mp="25eexlt3b9jd4">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1580" data-mp="1rhi4fkzpzpt2">
                      Free Delivery
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1581" data-mp="1vjd7ov17od9h">
                      Orders over PKR 5,000
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-1582" data-mp="3px4nkca4vtdd">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center" data-id="element-1583" data-mp="3puif8aff59u3">
                    <ShieldCheckIcon className="w-5 h-5 text-green-600" data-id="element-1584" data-mp="1e6wtbi1vn0kv" />
                  </div>
                  <div data-id="element-1585" data-mp="1a5urqukhmcfq">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1586" data-mp="1nysvb4733aud">
                      Warranty
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1587" data-mp="1j7b3b5n1gklj">1 Year Warranty</p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-1588" data-mp="1xvp5decms511">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center" data-id="element-1589" data-mp="1syoyx2bvd09e">
                    <RefreshCwIcon className="w-5 h-5 text-primary-600" data-id="element-1590" data-mp="26y6qtqkgotmz" />
                  </div>
                  <div data-id="element-1591" data-mp="21ylrhf1dqj9q">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1592" data-mp="22057r5n1cubi">
                      Easy Returns
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1593" data-mp="cm05hyxuswgy">7 Days Return</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product details tabs */}
        <div className="bg-white rounded-xl shadow-card p-6 mb-8" data-id="element-1594" data-mp="qj3noaum5z5q">
          <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1595" data-mp="qhshk4dp4fbg">
            Product Details
          </h2>

          <div className="prose max-w-none text-gray-600 mb-6" data-id="element-1596" data-mp="mjmg9bvmmrxx">
            <p data-id="element-1597" data-mp="hjqku10zw8tz">{product.description}</p>
          </div>

          {/* Specifications */}
          <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1598" data-mp="1e7ms2fajlrsj">
            Specifications
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-1599" data-mp="1a8e09fz2c3zm">
            {Object.entries(product.specifications).map(([key, value]) => <div key={key} className="flex border-b border-gray-100 py-2" data-id="element-1600" data-mp="15e9uv15g440n">
                <span className="text-gray-500 w-1/2" data-id="element-1601" data-mp="10grqlo2dicyz">{key}</span>
                <span className="text-gray-900 font-medium w-1/2" data-id="element-1602" data-mp="1xwx6lpyjy6p2">{value}</span>
              </div>)}
          </div>

          {/* Features */}
          <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1603" data-mp="1t4nmwro162v8">
            Key Features
          </h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-1604" data-mp="20j7rpha2ssnu">
            {product.features.map((feature, index) => <li key={index} className="flex items-center gap-2 text-gray-600" data-id="element-1605" data-mp="25i13945sy9ld" data-mpi={index}>
                <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1606" data-mp="2adzpom7klhj5" />
                {feature}
              </li>)}
          </ul>
        </div>

        {/* Related products */}
        {relatedProducts.length > 0 && <div className="mb-8" data-id="element-1607" data-mp="2je4tkmdk1xsk">
            <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1608" data-mp="2oeivdmokcbi5">
              Related Products
            </h2>
            <ProductGrid products={relatedProducts} columns={4} data-id="element-1609" data-mp="2t6treeg6nd5f" />
          </div>}
      </div>
    </div>;
}